import { useNode } from "@craftjs/core";
import { useThemeStore } from "@state/theme";
import { Button, IconButton, TextField, Autocomplete } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useRef } from "react";
import { apiClient } from "@api/index";
import { useAuthStore } from "@state/auth";

export interface CarouselItem {
  title: string;
  categoryID: string;
  categoryName: string; // <--- added
  imageURL: string;
}

interface CategoryCarouselProps {
  title?: string;
  connect?: any;
  drag?: any;
  items?: CarouselItem[];
}

const CategoryCarouselBuilderComponent = (props: CategoryCarouselProps) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  return (
    <CategoryCarouselPreviewComponent
      {...props}
      connect={connect}
      drag={drag}
    />
  );
};

export default CategoryCarouselBuilderComponent;

/**
 * PREVIEW COMPONENT
 */
export const CategoryCarouselPreviewComponent = ({
  title,
  items = [],
  connect,
  drag,
}: CategoryCarouselProps) => {
  const { textColor, accentColor } = useThemeStore();
  const [isHovering, setIsHovering] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const itemsRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const measureItemWidth = () => {
    if (!itemsRef.current) return;
    const firstItem = itemsRef.current.children[0] as HTMLElement | null;
    if (firstItem) {
      const width = firstItem.getBoundingClientRect().width + 16;
      setItemWidth(width);
    }
  };

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    measureItemWidth();
    setCurrentIndex((prev) => prev + 1);
  };

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    measureItemWidth();
    setCurrentIndex((prev) => prev - 1);
  };

  return (
    <div
      ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
      className="relative pt-4 pb-6"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {title && (
        <div className="flex flex-row ml-4 pb-2 items-center gap-4">
          <h2 className="mb-1 text-lg font-bold" style={{ color: textColor }}>
            {title}
          </h2>
        </div>
      )}

      <div className="relative w-full overflow-hidden">
        <div
          ref={itemsRef}
          className="flex space-x-4 px-4 hide-scrollbar transition-all duration-700"
          style={{
            transform: `translateX(-${currentIndex * itemWidth}px)`,
          }}
        >
          {(items || []).map((item: CarouselItem, index: number) => (
            <div
              key={index}
              className="relative w-48 h-28 rounded-lg overflow-hidden shadow-md flex-shrink-0"
            >
              <img
                src={item.imageURL}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 text-white p-2 text-sm font-semibold text-center">
                {item.title}
                {/* Optionally, you can also display the category name here */}
                {/* <p>{item.categoryName}</p> */}
              </div>
            </div>
          ))}
        </div>

        {isHovering && currentIndex > 0 && (
          <button
            className="absolute h-full left-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handlePrevClick}
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
              <path d="M15.4 4.6a1 1 0 0 0-1.41 0L6.7 11.3a1 1 0 0 0 0 1.4l7.3 7.3a1 1 0 1 0 1.4-1.4L9.8 12l5.6-5.7a1 1 0 0 0 0-1.4z"></path>
            </svg>
          </button>
        )}

        {isHovering && currentIndex < items.length - 1 && (
          <button
            className="absolute h-full right-0 top-1/2 transform -translate-y-1/2 p-3"
            style={{ color: accentColor }}
            onClick={handleNextClick}
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
              <path d="M8.6 4.6a1 1 0 0 1 1.4 0l7.3 7.3a1 1 0 0 1 0 1.4l-7.3 7.3a1 1 0 1 1-1.4-1.4L14.2 12 8.6 6.3a1 1 0 0 1 0-1.4z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

//
// SETTINGS PANEL
//
const CategoryCarouselSettings = () => {
  const {
    actions: { setProp },
    title,
    items,
  } = useNode((node) => ({
    title: node.data.props.title,
    items: node.data.props.items || [],
  }));

  const user = useAuthStore((state) => state.user);
  const { data: categories = [] } = apiClient.category.getClientCategories.useQuery(
    { clientID: user.clientID as string },
    { refetchOnWindowFocus: false }
  );

  const categoryOptions = categories.map((category) => ({
    label: category.displayName,
    value: category.categoryID,
  }));

  const handleTitleChange = (value: string) => {
    setProp((props: any) => {
      props.title = value;
    });
  };

  // You can still use a generic helper for property updates,
  // but for the autocomplete, we’ll specifically set both categoryID + categoryName
  const handleItemChange = (
    index: number,
    key: keyof CarouselItem,
    value: string
  ) => {
    setProp((props: any) => {
      if (!props.items) props.items = [];
      props.items[index][key] = value;
    });
  };

  const addItem = () => {
    setProp((props: any) => {
      props.items = [
        ...(props.items || []),
        {
          title: "",
          categoryID: "",
          categoryName: "", // Initialize with empty string
          imageURL: "",
        },
      ];
    });
  };

  const removeItem = (index: number) => {
    setProp((props: any) => {
      props.items = (props.items || []).filter(
        (_: CarouselItem, i: number) => i !== index
      );
    });
  };

  return (
    <div className="space-y-6">
      <TextField
        label="Carousel Title"
        fullWidth
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
      />

      {(items || []).map((item: CarouselItem, index: number) => {
        const categoryOptionsValue =
          categoryOptions.find((opt) => opt.value === item.categoryID) || null;

        return (
          <div
            key={index}
            className="border border-gray-300 p-4 rounded-md space-y-2 bg-gray-50"
          >
            {/* Title row with delete button on the right */}
            <div className="flex items-center gap-2">
              <TextField
                label="Item Title"
                size="small"
                value={item.title}
                onChange={(e) =>
                  handleItemChange(index, "title", e.target.value)
                }
              />
              <IconButton onClick={() => removeItem(index)} color="error">
                <DeleteIcon />
              </IconButton>
            </div>

            <Autocomplete
              size="small"
              options={categoryOptions}
              value={categoryOptionsValue}
              onChange={(_, newValue) => {
                setProp((props: any) => {
                  if (!props.items) props.items = [];
                  // If user clears the Autocomplete, reset ID and name
                  props.items[index].categoryID = newValue?.value || "";
                  props.items[index].categoryName = newValue?.label || "";
                });
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Category" fullWidth />
              )}
            />

            <TextField
              label="Image URL"
              fullWidth
              size="small"
              value={item.imageURL}
              onChange={(e) =>
                handleItemChange(index, "imageURL", e.target.value)
              }
            />
          </div>
        );
      })}

      <Button startIcon={<AddIcon />} variant="outlined" onClick={addItem}>
        Add Item
      </Button>
    </div>
  );
};

CategoryCarouselBuilderComponent.craft = {
  props: {
    title: "Category Carousel",
    items: [],
  },
  displayName: "Category Carousel",
  related: {
    settings: CategoryCarouselSettings,
  },
};
